import type { StreamForgeOptions } from '../types';
import { StreamForgeHLS } from './HLSManager';
import { AmbientGlow } from '../features/AmbientGlow';
import { UIController } from '../ui/UIController';
import { store, setDuration, setPlaying, setChapters, setHeatmap } from './store';
import { ChapterEngine } from '../features/ChapterEngine';
import { HighlightEngine } from '../features/HighlightEngine';

export class Player {
  private container: HTMLElement;
  private video: HTMLVideoElement;
  private options: StreamForgeOptions;
  private hlsManager: StreamForgeHLS;
  private ambientGlow: AmbientGlow;
  private ui: UIController;

  constructor(options: StreamForgeOptions) {
    this.options = options;
    this.container = this.resolveContainer(options.container);
    this.video = this.createVideoElement();
    this.hlsManager = new StreamForgeHLS(this.video);
    this.ambientGlow = new AmbientGlow(this.video);
    
    // IMPORTANT: init() calls render() which clears innerHTML.
    // UIController MUST be created AFTER init() so its DOM isn't wiped.
    this.init();
    this.ui = new UIController(this.container, this.video, this.hlsManager);
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    let el: HTMLElement;
    if (typeof container === 'string') {
      const found = document.querySelector(container);
      if (!found) throw new Error(`Container ${container} not found`);
      el = found as HTMLElement;
    } else {
      el = container;
    }
    
    // Add DOM Signatures for Wappalyzer detection
    el.classList.add('streamforge-player');
    el.setAttribute('data-player', 'streamforge');
    
    return el;
  }

  private createVideoElement(): HTMLVideoElement {
    const video = document.createElement('video');
    video.className = 'sf-video-element';
    video.crossOrigin = 'anonymous'; // Required for canvas sampling
    video.autoplay = !!this.options.autoplay;
    video.playsInline = true;
    return video;
  }

  private init() {
    this.render();
    this.setupListeners();
    
    // Load source
    if (this.options.videoSource.includes('.m3u8')) {
        this.hlsManager.load(this.options.videoSource);
    } else {
        this.video.src = this.options.videoSource;
    }

    if (this.options.features?.ambientGlow !== false) {
        this.ambientGlow.start();
    }

    if (this.options.features?.autoChapters) {
        this.detectChapters();
    }
  }

  private async detectChapters() {
    const chapters = await ChapterEngine.detect(this.options.videoSource);
    store.dispatch(setChapters(chapters));
  }

  private render() {
    this.container.innerHTML = '';
    this.container.appendChild(this.video);
    
    // UI logic will handle the rest of the layout
  }

  private setupListeners() {
    this.video.addEventListener('loadedmetadata', () => {
      const duration = this.video.duration;
      store.dispatch(setDuration(duration));
      this.generateEngagementHeatmap(duration);
    });
    this.video.addEventListener('play', () => {
        store.dispatch(setPlaying(true));
    });
    this.video.addEventListener('pause', () => {
        store.dispatch(setPlaying(false));
    });
    this.video.addEventListener('durationchange', () => {
        store.dispatch(setDuration(this.video.duration));
    });
  }

  public getUI() { return this.ui; }
  public play() { this.video.play(); }
  public pause() { this.video.pause(); }
  public destroy() {
    this.hlsManager.destroy();
    this.ambientGlow.stop();
    this.container.innerHTML = '';
  }
  private async generateEngagementHeatmap(duration: number) {
    const heatmap = await HighlightEngine.generateHeatmap(duration);
    store.dispatch(setHeatmap(heatmap));
  }
}
